import axios from 'axios';
import { header } from './header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const PRODUCT_CACHE_DB = 'pegasus-product-cache';
const PRODUCT_CACHE_STORE = 'keyval';
const PRODUCT_CACHE_KEY = 'products-stream-v1';
export const PRODUCT_CACHE_UPDATED_EVENT = 'pegasus:products-cache-updated';

let productsRefreshPromise = null;

const canUseIndexedDb = () => typeof window !== 'undefined' && Boolean(window.indexedDB);

const openProductCacheDb = () =>
  new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }

    const request = window.indexedDB.open(PRODUCT_CACHE_DB, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PRODUCT_CACHE_STORE)) {
        db.createObjectStore(PRODUCT_CACHE_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const readProductsCache = async () => {
  try {
    const db = await openProductCacheDb();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(PRODUCT_CACHE_STORE, 'readonly');
      const request = transaction.objectStore(PRODUCT_CACHE_STORE).get(PRODUCT_CACHE_KEY);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    try {
      const raw = window.localStorage.getItem(PRODUCT_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }
};

const writeProductsCache = async (products) => {
  const payload = {
    products,
    updatedAt: new Date().toISOString(),
    count: Array.isArray(products) ? products.length : 0,
  };

  try {
    const db = await openProductCacheDb();
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(PRODUCT_CACHE_STORE, 'readwrite');
      transaction.objectStore(PRODUCT_CACHE_STORE).put(payload, PRODUCT_CACHE_KEY);
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    try {
      window.localStorage.setItem(PRODUCT_CACHE_KEY, JSON.stringify(payload));
    } catch (_) {
      // Ignore storage quota errors; the fresh stream result is still returned to the caller.
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PRODUCT_CACHE_UPDATED_EVENT, { detail: payload }));
  }

  return payload;
};

const fetchProductsStream = async () => {
  console.log('Chargement des produits via streaming NDJSON...');
  const startTime = Date.now();

  const response = await axios.get(
    `${API_URL}/products/stream`,
    {
      ...header(),
      responseType: 'text',
    }
  );

  const ndjsonText = response.data;
  const lines = ndjsonText.split('\n').filter(line => line.trim() !== '');
  const allProducts = [];
  let parseErrors = 0;

  for (const line of lines) {
    try {
      allProducts.push(JSON.parse(line));
    } catch (parseError) {
      parseErrors++;
      console.warn('[ProductService] Erreur de parsing d\'une ligne NDJSON:', parseError);
    }
  }

  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(2);
  console.log(`[ProductService] ${allProducts.length} produits chargés en ${elapsedTime}s`);

  if (parseErrors > 0) {
    console.warn(`[ProductService] ${parseErrors} lignes n'ont pas pu être parsées`);
  }

  await writeProductsCache(allProducts);
  return allProducts;
};

export const refreshProductsInBackground = () => {
  if (!productsRefreshPromise) {
    productsRefreshPromise = fetchProductsStream().finally(() => {
      productsRefreshPromise = null;
    });
  }
  return productsRefreshPromise;
};

export const fetchProducts = async (options = {}) => {
  const { forceRefresh = false, cacheFirst = true, backgroundRefresh = true } = options || {};
  try {
    if (!forceRefresh && cacheFirst) {
      const cached = await readProductsCache();
      if (Array.isArray(cached?.products) && cached.products.length > 0) {
        if (backgroundRefresh) {
          refreshProductsInBackground().catch((error) => {
            console.warn('[ProductService] Refresh produits en arrière-plan échoué:', error);
          });
        }
        return cached.products;
      }
    }

    return await refreshProductsInBackground();
  } catch (error) {
    console.error('[ProductService] Erreur lors du chargement des produits via streaming:', error);
    throw error;
  }
};

export const fetchTodayCount = async () => {
  const res = await axios.get(`${API_URL}/product/today`, header());
  return res.data;
};

export default { fetchProducts, fetchTodayCount, refreshProductsInBackground, PRODUCT_CACHE_UPDATED_EVENT };
